import {useEffect, useMemo, useState} from "react";
import {Card, Col, Collapse, Empty, List, Row, Table, Tag} from "antd";
import {TeamOutlined} from "@ant-design/icons";
import AxiosServices from "../../Services/axiosServices";
import {IClassement} from "../../Interfaces/IClassement";
import {IUsrSqdCmpEdt} from "../../Interfaces/IUsrSqdCmpEdt";
import {ISqd} from "../../Interfaces/ISqd";
import {useGlobal} from "../../config/GlobalContext";
import {useAuth} from "../../config/AuthContext";

const ACCENT = '#203923';

type SquadGroup = {
    sqd: ISqd;
    items: IUsrSqdCmpEdt[];
    hasOpen: boolean;
};

const Classement = () => {

    const [classement, setClassement] = useState<IClassement[]>([]);
    const [mySquads, setMySquads] = useState<IUsrSqdCmpEdt[]>([]);
    const {currentCmpEdt, setCurrentCmpEdt} = useGlobal();
    const {isLoggedIn, authUser} = useAuth();

    useEffect(() => {
        if (!isLoggedIn || !authUser?.idt || authUser.idt <= 0) {
            setMySquads([]);
            return;
        }
        AxiosServices.getMySquads(authUser.idt)
            .then(setMySquads)
            .catch(err => console.error(err));
    }, [isLoggedIn, authUser?.idt]);

    useEffect(() => {
        if (!currentCmpEdt) {
            setClassement([]);
            return;
        }
        AxiosServices.getClassement(setClassement, currentCmpEdt)
            .then()
            .catch(err => console.error(err));
    }, [currentCmpEdt]);

    const squadGroups: SquadGroup[] = useMemo(() => {
        const map = new Map<number, SquadGroup>();
        for (const item of mySquads) {
            const sqdIdt = item.Sqd?.idt;
            if (sqdIdt === undefined) continue;
            if (!map.has(sqdIdt)) {
                map.set(sqdIdt, {sqd: item.Sqd, items: [], hasOpen: false});
            }
            const group = map.get(sqdIdt)!;
            group.items.push(item);
            if (item.CmpEdt?.etOpn === 'O') group.hasOpen = true;
        }
        const groups = Array.from(map.values());

        for (const g of groups) {
            g.items.sort((a, b) => {
                const ao = a.CmpEdt?.etOpn === 'O' ? 0 : 1;
                const bo = b.CmpEdt?.etOpn === 'O' ? 0 : 1;
                if (ao !== bo) return ao - bo;
                return (a.CmpEdt?.Lib || '').localeCompare(b.CmpEdt?.Lib || '');
            });
        }

        groups.sort((a, b) => {
            if (a.hasOpen !== b.hasOpen) return a.hasOpen ? -1 : 1;
            return (a.sqd?.Lib || '').localeCompare(b.sqd?.Lib || '');
        });

        return groups;
    }, [mySquads]);

    const defaultOpenKeys = useMemo(
        () => squadGroups.filter(g => g.hasOpen).map(g => String(g.sqd.idt)),
        [squadGroups],
    );

    const columns = [
        {title: 'Rang', dataIndex: 'Rang', key: 'Rang'},
        {title: 'Parieur', dataIndex: 'Parieur', key: 'Parieur'},
        {title: 'Total', dataIndex: 'Total', key: 'Total'},
        {title: 'Score exact', dataIndex: 'Score_exact', key: 'Score_exact'},
        {title: 'Différence de but', dataIndex: 'Différence_de_but', key: 'Différence_de_but'},
        {title: 'Bon pronostic', dataIndex: 'Bon_pronostic', key: 'Bon_pronostic'},
        {title: 'Buts Domicile', dataIndex: 'Buts_Domicile', key: 'Buts_Domicile'},
        {title: 'Buts Extérieur', dataIndex: 'Buts_Extérieur', key: 'Buts_Extérieur'},
        {title: 'Pari effectué', dataIndex: 'Pari_effectué', key: 'Pari_effectué'},
    ];

    const squadsEmptyText = isLoggedIn
        ? "Tu n'appartiens à aucune squad."
        : "Connecte-toi pour voir tes squads.";

    const collapseItems = squadGroups.map(g => ({
        key: String(g.sqd.idt),
        label: (
            <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <TeamOutlined style={{color: ACCENT}}/>
                <span style={{fontWeight: 600}}>{g.sqd.Lib || `Squad #${g.sqd.idt}`}</span>
                {g.hasOpen && <Tag color="green" style={{marginLeft: 'auto'}}>Active</Tag>}
            </span>
        ),
        children: (
            <List
                size="small"
                dataSource={g.items}
                renderItem={(item) => {
                    const isActive = item.CmpEdt?.idt === currentCmpEdt;
                    const isOpen = item.CmpEdt?.etOpn === 'O';
                    return (
                        <List.Item
                            style={{
                                cursor: 'pointer',
                                background: isActive ? '#e6f7ff' : undefined,
                                padding: '8px 12px',
                                borderLeft: isActive ? `3px solid ${ACCENT}` : '3px solid transparent',
                            }}
                            onClick={() => item.CmpEdt?.idt && setCurrentCmpEdt(item.CmpEdt.idt)}
                        >
                            <div style={{display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center'}}>
                                <span style={{fontWeight: isActive ? 600 : 400, fontSize: 13}}>
                                    {item.CmpEdt?.Lib || `Compétition #${item.CmpEdt?.idt}`}
                                </span>
                                {isOpen
                                    ? <Tag color="green">Ouverte</Tag>
                                    : <Tag color="default">Fermée</Tag>
                                }
                            </div>
                        </List.Item>
                    );
                }}
            />
        ),
    }));

    return (
        <Row gutter={16} style={{padding: 16}} wrap>
            <Col xs={24} md={8} lg={6}>
                <Card
                    title={<><TeamOutlined/> Mes squads</>}
                    styles={{header: {background: ACCENT, color: '#fff'}}}
                >
                    {squadGroups.length === 0 ? (
                        <Empty description={squadsEmptyText}/>
                    ) : (
                        <Collapse
                            key={squadGroups.map(g => g.sqd.idt).join('-')}
                            defaultActiveKey={defaultOpenKeys}
                            ghost
                            items={collapseItems}
                        />
                    )}
                </Card>
            </Col>
            <Col xs={24} md={16} lg={18}>
                <Card title="Classement" styles={{header: {background: ACCENT, color: '#fff'}}}>
                    <Table
                        dataSource={classement}
                        columns={columns}
                        rowKey={(r) => `${r.Rang}-${r.Parieur}`}
                        pagination={false}
                    />
                </Card>
            </Col>
        </Row>
    );
};

export default Classement;
